import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckSquare2,
  ChevronDown,
  ChevronRight,
  FileText,
  FolderOpen,
  Pencil,
  Plus,
  PlusCircle,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { KnowledgeBase, KnowledgeBaseCategory } from '@/components/knowledge/types';
import { useUserDesktopConfig } from '@/OS/hooks/useUserSettings';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const COLLAPSED_CATEGORIES_STORAGE_KEY = 'knowledge-base-home-collapsed-categories';
const COLLAPSED_CATEGORIES_CONFIG_KEY = 'ai_knowledge_home_collapsed_categories_v1';

function parseCollapsedCategoryIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === 'string')
        : [];
    } catch {
      return [];
    }
  }

  return [];
}

interface KnowledgeCategoryHomeProps {
  knowledgeBases: KnowledgeBase[];
  categories: KnowledgeBaseCategory[];
  assignmentByKnowledgeBaseId: Record<string, string>;
  loading?: boolean;
  onOpenKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onCreateKnowledgeBaseInCategory: (categoryId: string | null, categoryLabel: string) => void;
  onEditKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onDeleteKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onDeleteSelectedKnowledgeBases: (knowledgeBaseIds: string[]) => void;
  onCreateCategory: (name: string) => Promise<void>;
  onMoveKnowledgeBasesToCategory: (knowledgeBaseIds: string[], categoryId: string | null) => Promise<boolean>;
}

interface KnowledgeBaseCardProps {
  knowledgeBase: KnowledgeBase;
  selected: boolean;
  onToggleSelected: (knowledgeBaseId: string) => void;
  onOpenKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onEditKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onDeleteKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
}

interface ExplorerSectionProps {
  title: string;
  color?: string | null;
  categoryId: string | null;
  knowledgeBases: KnowledgeBase[];
  selectedIds: string[];
  categories: KnowledgeBaseCategory[];
  collapsed: boolean;
  moveMenuOpen: boolean;
  isDropTarget: boolean;
  emptyMessage: string;
  onToggleCollapsed: () => void;
  onToggleMoveMenu: () => void;
  onMoveSelected: (categoryId: string | null) => void;
  onCreateKnowledgeBaseInCategory: (categoryId: string | null, categoryLabel: string) => void;
  onToggleKnowledgeBaseSelected: (knowledgeBaseId: string) => void;
  onToggleSelectAll: (knowledgeBaseIds: string[]) => void;
  onOpenKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onEditKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onDeleteKnowledgeBase: (knowledgeBase: KnowledgeBase) => void;
  onDeleteSelectedKnowledgeBases: (knowledgeBaseIds: string[]) => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

function KnowledgeBaseCard({
  knowledgeBase,
  selected,
  onToggleSelected,
  onOpenKnowledgeBase,
  onEditKnowledgeBase,
  onDeleteKnowledgeBase,
}: KnowledgeBaseCardProps) {
  return (
    <div
      className={cn(
        'group relative rounded-2xl border bg-background/90 p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-md',
        selected ? 'border-primary bg-accent/10 shadow-md' : 'border-border/60'
      )}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/knowledge-base-id', knowledgeBase.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-8 w-8 rounded-full', selected && 'text-primary')}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelected(knowledgeBase.id);
          }}
          aria-label={selected ? `Deselect ${knowledgeBase.name}` : `Select ${knowledgeBase.name}`}
        >
          {selected ? <CheckSquare2 className="h-4 w-4" /> : <Square className="h-4 w-4" />}
        </Button>

        <div className="flex items-center gap-1 opacity-80 transition-opacity group-hover:opacity-100">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-full"
            onClick={(event) => {
              event.stopPropagation();
              onEditKnowledgeBase(knowledgeBase);
            }}
            aria-label={`Rename ${knowledgeBase.name}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteKnowledgeBase(knowledgeBase);
            }}
            aria-label={`Delete ${knowledgeBase.name}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <button
        type="button"
        className="block w-full text-left"
        onClick={() => onOpenKnowledgeBase(knowledgeBase)}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-muted p-2 text-muted-foreground transition-colors group-hover:bg-accent">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-foreground">{knowledgeBase.name}</div>
            <div className="mt-1 line-clamp-2 min-h-[2.5rem] text-xs text-muted-foreground">
              {knowledgeBase.description?.trim() || 'Knowledge base'}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>{knowledgeBase.documents_count || 0} docs</span>
          <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-medium uppercase tracking-wide">
            Open in tab
          </span>
        </div>
      </button>
    </div>
  );
}

function ExplorerSection({
  title,
  color,
  categoryId,
  knowledgeBases,
  selectedIds,
  categories,
  collapsed,
  moveMenuOpen,
  isDropTarget,
  emptyMessage,
  onToggleCollapsed,
  onToggleMoveMenu,
  onMoveSelected,
  onCreateKnowledgeBaseInCategory,
  onToggleKnowledgeBaseSelected,
  onToggleSelectAll,
  onOpenKnowledgeBase,
  onEditKnowledgeBase,
  onDeleteKnowledgeBase,
  onDeleteSelectedKnowledgeBases,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: ExplorerSectionProps) {
  const allSelected = knowledgeBases.length > 0 && selectedIds.length === knowledgeBases.length;

  return (
    <section
      className={cn(
        'relative h-fit rounded-2xl border border-border/70 bg-card/75 p-4 transition-all',
        collapsed ? 'xl:col-span-1' : 'xl:col-span-2',
        isDropTarget && 'border-primary bg-accent/15'
      )}
      style={color ? { boxShadow: `inset 0 0 0 1px ${color}30` } : undefined}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <button
          type="button"
          className="flex min-w-0 items-start gap-3 text-left"
          onClick={onToggleCollapsed}
        >
          <div className="mt-0.5 rounded-lg bg-muted p-1.5 text-muted-foreground">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <h3 className="truncate text-base font-semibold text-foreground">{title}</h3>
              {color && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {knowledgeBases.length} knowledge base{knowledgeBases.length === 1 ? '' : 's'}
              {selectedIds.length > 0 && <span className="ml-2 font-medium text-foreground">• {selectedIds.length} selected</span>}
              {collapsed && knowledgeBases.length > 0 && <span className="ml-2">• collapsed</span>}
            </p>
          </div>
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-9 w-9 rounded-xl"
            onClick={() => onCreateKnowledgeBaseInCategory(categoryId, title)}
            aria-label={`Create knowledge base in ${title}`}
          >
            <Plus className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-9 w-9 rounded-xl"
            onClick={() => onToggleSelectAll(knowledgeBases.map((knowledgeBase) => knowledgeBase.id))}
            disabled={knowledgeBases.length === 0}
            aria-label={allSelected ? `Deselect all in ${title}` : `Select all in ${title}`}
          >
            {allSelected ? <CheckSquare2 className="h-4 w-4" /> : <Square className="h-4 w-4" />}
          </Button>

          {selectedIds.length > 0 && (
            <div className="relative flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-xl bg-background/90"
                onClick={onToggleMoveMenu}
              >
                Move to...
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>

              {moveMenuOpen && (
                <div className="absolute right-0 top-11 z-50 min-w-[190px] rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                      categoryId === null && 'opacity-50'
                    )}
                    onClick={() => onMoveSelected(null)}
                    disabled={categoryId === null}
                  >
                    Uncategorized
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={cn(
                        'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                        category.id === categoryId && 'opacity-50'
                      )}
                      onClick={() => onMoveSelected(category.id)}
                      disabled={category.id === categoryId}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}

              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-9 w-9 rounded-xl text-muted-foreground hover:text-destructive"
                onClick={() => onDeleteSelectedKnowledgeBases(selectedIds)}
                aria-label={`Delete selected knowledge bases in ${title}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {collapsed ? (
        <div className="">
        </div>
      ) : knowledgeBases.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {knowledgeBases.map((knowledgeBase) => (
            <KnowledgeBaseCard
              key={knowledgeBase.id}
              knowledgeBase={knowledgeBase}
              selected={selectedIds.includes(knowledgeBase.id)}
              onToggleSelected={onToggleKnowledgeBaseSelected}
              onOpenKnowledgeBase={onOpenKnowledgeBase}
              onEditKnowledgeBase={onEditKnowledgeBase}
              onDeleteKnowledgeBase={onDeleteKnowledgeBase}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
          <p>{emptyMessage}</p>
          <Button
            type="button"
            variant="outline"
            className="mt-4 rounded-xl"
            onClick={() => onCreateKnowledgeBaseInCategory(categoryId, title)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add knowledge base
          </Button>
        </div>
      )}
    </section>
  );
}

export default function KnowledgeCategoryHome({
  knowledgeBases,
  categories,
  assignmentByKnowledgeBaseId,
  loading = false,
  onOpenKnowledgeBase,
  onCreateKnowledgeBaseInCategory,
  onEditKnowledgeBase,
  onDeleteKnowledgeBase,
  onDeleteSelectedKnowledgeBases,
  onCreateCategory,
  onMoveKnowledgeBasesToCategory,
}: KnowledgeCategoryHomeProps) {
  const { user } = useAuth();
  const { desktopConfig, isLoaded: desktopConfigLoaded, saveDesktopConfig } = useUserDesktopConfig();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeDropCategoryId, setActiveDropCategoryId] = useState<string | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<string[]>([]);
  const [hasHydratedCollapsedCategoryIds, setHasHydratedCollapsedCategoryIds] = useState(false);
  const [moveMenuCategoryId, setMoveMenuCategoryId] = useState<string | null>(null);
  const lastSavedCollapsedCategoryIdsRef = useRef('');

  const groupedKnowledgeBases = useMemo(() => {
    const groups = new Map<string | null, KnowledgeBase[]>();
    groups.set(null, []);
    categories.forEach((category) => groups.set(category.id, []));

    knowledgeBases.forEach((knowledgeBase) => {
      const categoryId = assignmentByKnowledgeBaseId[knowledgeBase.id] ?? null;
      if (!groups.has(categoryId)) {
        groups.set(categoryId, []);
      }
      groups.get(categoryId)?.push(knowledgeBase);
    });

    groups.forEach((items) => {
      items.sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [assignmentByKnowledgeBaseId, categories, knowledgeBases]);

  useEffect(() => {
    if (hasHydratedCollapsedCategoryIds || !desktopConfigLoaded) {
      return;
    }

    const remoteCollapsedCategoryIds = parseCollapsedCategoryIds(desktopConfig[COLLAPSED_CATEGORIES_CONFIG_KEY]);
    let nextCollapsedCategoryIds = remoteCollapsedCategoryIds;

    if (nextCollapsedCategoryIds.length === 0 && typeof window !== 'undefined') {
      nextCollapsedCategoryIds = parseCollapsedCategoryIds(
        window.localStorage.getItem(COLLAPSED_CATEGORIES_STORAGE_KEY)
      );
    }

    setCollapsedCategoryIds(nextCollapsedCategoryIds);
    lastSavedCollapsedCategoryIdsRef.current = JSON.stringify(nextCollapsedCategoryIds);
    setHasHydratedCollapsedCategoryIds(true);
  }, [desktopConfig, desktopConfigLoaded, hasHydratedCollapsedCategoryIds]);

  useEffect(() => {
    const validCategoryIds = new Set(categories.map((category) => category.id));
    validCategoryIds.add('uncategorized');

    setCollapsedCategoryIds((current) => {
      const next = current.filter((categoryId) => validCategoryIds.has(categoryId));
      return next.length === current.length ? current : next;
    });
  }, [categories]);

  useEffect(() => {
    if (!hasHydratedCollapsedCategoryIds) {
      return;
    }

    const serialized = JSON.stringify(collapsedCategoryIds);
    if (serialized === lastSavedCollapsedCategoryIdsRef.current) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSED_CATEGORIES_STORAGE_KEY, serialized);
    }

    lastSavedCollapsedCategoryIdsRef.current = serialized;

    if (user) {
      void saveDesktopConfig(COLLAPSED_CATEGORIES_CONFIG_KEY, collapsedCategoryIds);
    }
  }, [collapsedCategoryIds, hasHydratedCollapsedCategoryIds, saveDesktopConfig, user]);

  const toggleCollapsed = (categoryKey: string) => {
    setCollapsedCategoryIds((current) => (
      current.includes(categoryKey)
        ? current.filter((id) => id !== categoryKey)
        : [...current, categoryKey]
    ));
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (name.length < 2) return;

    setCreatingCategory(true);
    await onCreateCategory(name);
    setCreatingCategory(false);
    setNewCategoryName('');
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>, categoryId: string | null) => {
    event.preventDefault();
    setActiveDropCategoryId(null);
    const knowledgeBaseId = event.dataTransfer.getData('text/knowledge-base-id');
    if (!knowledgeBaseId) return;
    await onMoveKnowledgeBasesToCategory([knowledgeBaseId], categoryId);
    setSelectedIds((current) => current.filter((id) => id !== knowledgeBaseId));
  };

  const toggleKnowledgeBaseSelected = (knowledgeBaseId: string) => {
    setSelectedIds((current) => (
      current.includes(knowledgeBaseId)
        ? current.filter((id) => id !== knowledgeBaseId)
        : [...current, knowledgeBaseId]
    ));
  };

  const toggleSelectAll = (knowledgeBaseIds: string[]) => {
    if (knowledgeBaseIds.length === 0) return;

    setSelectedIds((current) => {
      const allSelected = knowledgeBaseIds.every((id) => current.includes(id));
      if (allSelected) {
        return current.filter((id) => !knowledgeBaseIds.includes(id));
      }

      return Array.from(new Set([...current, ...knowledgeBaseIds]));
    });
  };

  const handleMoveSelectedKnowledgeBases = async (knowledgeBaseIds: string[], categoryId: string | null) => {
    const moved = await onMoveKnowledgeBasesToCategory(knowledgeBaseIds, categoryId);
    if (moved) {
      setSelectedIds((current) => current.filter((id) => !knowledgeBaseIds.includes(id)));
      setMoveMenuCategoryId(null);
    }
    return moved;
  };

  const handleDeleteSelectedKnowledgeBases = (knowledgeBaseIds: string[]) => {
    onDeleteSelectedKnowledgeBases(knowledgeBaseIds);
    setSelectedIds((current) => current.filter((id) => !knowledgeBaseIds.includes(id)));
    setMoveMenuCategoryId(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 rounded-2xl border border-border/70 bg-card/70 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Home</h2>
          <p className="text-sm text-muted-foreground">
            Organize, create, and open knowledge bases from one place.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
          <Input
            value={newCategoryName}
            onChange={(event) => setNewCategoryName(event.target.value)}
            placeholder="Create a category"
            className="sm:w-56"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleCreateCategory();
              }
            }}
          />
          <Button onClick={() => void handleCreateCategory()} disabled={creatingCategory || newCategoryName.trim().length < 2}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add category
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-border/70 bg-card/70 p-8 text-center text-muted-foreground">
          Loading categories…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {categories.map((category) => {
            const categoryKnowledgeBases = groupedKnowledgeBases.get(category.id) ?? [];
            const categorySelectedIds = categoryKnowledgeBases
              .map((knowledgeBase) => knowledgeBase.id)
              .filter((id) => selectedIds.includes(id));

            return (
              <ExplorerSection
                key={category.id}
                title={category.name}
                color={category.color}
                categoryId={category.id}
                knowledgeBases={categoryKnowledgeBases}
                selectedIds={categorySelectedIds}
                categories={categories}
                collapsed={collapsedCategoryIds.includes(category.id)}
                moveMenuOpen={moveMenuCategoryId === category.id}
                emptyMessage="No knowledge bases here yet."
                isDropTarget={activeDropCategoryId === category.id}
                onToggleCollapsed={() => toggleCollapsed(category.id)}
                onToggleMoveMenu={() => setMoveMenuCategoryId((current) => (current === category.id ? null : category.id))}
                onMoveSelected={(nextCategoryId) => {
                  void handleMoveSelectedKnowledgeBases(categorySelectedIds, nextCategoryId);
                }}
                onCreateKnowledgeBaseInCategory={onCreateKnowledgeBaseInCategory}
                onToggleKnowledgeBaseSelected={toggleKnowledgeBaseSelected}
                onToggleSelectAll={toggleSelectAll}
                onOpenKnowledgeBase={onOpenKnowledgeBase}
                onEditKnowledgeBase={onEditKnowledgeBase}
                onDeleteKnowledgeBase={onDeleteKnowledgeBase}
                onDeleteSelectedKnowledgeBases={handleDeleteSelectedKnowledgeBases}
                onDragEnter={() => setActiveDropCategoryId(category.id)}
                onDragLeave={() => setActiveDropCategoryId((current) => (current === category.id ? null : current))}
                onDragOver={(event) => {
                  event.preventDefault();
                  setActiveDropCategoryId(category.id);
                }}
                onDrop={(event) => void handleDrop(event, category.id)}
              />
            );
          })}

          {(() => {
            const uncategorizedKnowledgeBases = groupedKnowledgeBases.get(null) ?? [];
            const uncategorizedSelectedIds = uncategorizedKnowledgeBases
              .map((knowledgeBase) => knowledgeBase.id)
              .filter((id) => selectedIds.includes(id));

            return (
              <ExplorerSection
                title="Uncategorized"
                categoryId={null}
                knowledgeBases={uncategorizedKnowledgeBases}
                selectedIds={uncategorizedSelectedIds}
                categories={categories}
                collapsed={collapsedCategoryIds.includes('uncategorized')}
                moveMenuOpen={moveMenuCategoryId === 'uncategorized'}
                emptyMessage="No uncategorized knowledge bases yet."
                isDropTarget={activeDropCategoryId === 'uncategorized'}
                onToggleCollapsed={() => toggleCollapsed('uncategorized')}
                onToggleMoveMenu={() => setMoveMenuCategoryId((current) => (current === 'uncategorized' ? null : 'uncategorized'))}
                onMoveSelected={(nextCategoryId) => {
                  void handleMoveSelectedKnowledgeBases(uncategorizedSelectedIds, nextCategoryId);
                }}
                onCreateKnowledgeBaseInCategory={onCreateKnowledgeBaseInCategory}
                onToggleKnowledgeBaseSelected={toggleKnowledgeBaseSelected}
                onToggleSelectAll={toggleSelectAll}
                onOpenKnowledgeBase={onOpenKnowledgeBase}
                onEditKnowledgeBase={onEditKnowledgeBase}
                onDeleteKnowledgeBase={onDeleteKnowledgeBase}
                onDeleteSelectedKnowledgeBases={handleDeleteSelectedKnowledgeBases}
                onDragEnter={() => setActiveDropCategoryId('uncategorized')}
                onDragLeave={() => setActiveDropCategoryId((current) => (current === 'uncategorized' ? null : current))}
                onDragOver={(event) => {
                  event.preventDefault();
                  setActiveDropCategoryId('uncategorized');
                }}
                onDrop={(event) => void handleDrop(event, null)}
              />
            );
          })()}
        </div>
      )}
    </div>
  );
}